import { useEffect, useState } from "react";
import {
  BUTTON_CONNECT_ACCOUNT,
  BUTTON_CONNECT_TIKTOK,
} from "../../i18n/keysTranslations";
import {
  BUTTON_TIKTOK_BUSINESS_ACTION_ID,
  MODE_BACK_DROP_CONNECT,
  TIKTOK,
  TIKTOK_BUSINESS,
} from "../../utils/constants";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../utils/string";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  startConnectTiktokBusiness,
  startGetTokenTiktokBusiness,
} from "../../actions/user";
import { startAfterPlatformConnected } from "../../actions/agencyAccountLinking";
import { showPlatformConnectedAlert } from "../../utils/showPlatformConnectedAlert";
import IconButton from "./IconButton";
import Loader from "../Loaders/Loader";
import AddIcon from "@mui/icons-material/Add";
import { ConfirmAlert } from "../Alerts/Alerts";
import BackDropLoader from "../Loaders/BackDropLoader";
// import { getNameBusinessTheme } from "../../actions/getters";
import ConnectionProgressModal from "../Containers/ConnectionProgressModal";
import { createPortal } from "react-dom";
import Button from "./Button";

const ButtonTikTokBusinessAction = ({
  id = BUTTON_TIKTOK_BUSINESS_ACTION_ID,
  text = BUTTON_CONNECT_TIKTOK,
  prevBlockClick = false,
  onPrevBlockClick = () => {},
  onClick = () => {},
  onCallbackOpenPopup = () => {},
  disabled = false,
  sx = {},
  // scope = TIKTOK_BUSINESS_SCOPES,
  logo = TIKTOK_BUSINESS,
  showLogo = true,
  type = "button",
  mode = "verify",
  requirePlatformConnection = true,
  icon = <AddIcon />,
  disableRipple = false,
  showButtonIcon = false,
  doTranslation = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [externalPopup, setExternalPopup] = useState(null);
  const [modeBackDrop, setModeBackDrop] = useState();

  const dispatch = useDispatchApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness || {};
  const isTikTokBusinessConnected = Object.values(
    tikTokBusinessIntegrations
  ).some((integration) => integration.isActive);
  const { t } = useTranslationApp();
  // const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  const onConnectAccount = async () => {
    const result = await ConfirmAlert({
      icon: false,
      confirmButtonText: t(BUTTON_CONNECT_ACCOUNT),
      html: <ConnectionProgressModal platform={TIKTOK} />,
      width: "60vh",
    });

    if (!result.isConfirmed) {
      return;
    }

    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_POPUP);
    dispatch(
      startConnectTiktokBusiness({
        onChangeLoading: (loading) => {
          setIsLoading(loading);
          setOpenBackDrop(loading);
          if (!loading) {
            onCallbackOpenPopup();
          }
        },
        onChangePopupReference: (popup) => setExternalPopup(popup),
      })
    );
  };

  const onGetTokenTiktokBusiness = async ({ authCode, state }) => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_INFORMATION);
    setOpenBackDrop(true);
    const response = await dispatch(
      startGetTokenTiktokBusiness({ authCode, state, withAuth: true })
    );
    setOpenBackDrop(false);

    if (response.ok) {
      await showPlatformConnectedAlert({ t });
      await dispatch(
        startAfterPlatformConnected({
          platform: TIKTOK_BUSINESS,
          integration: response.data,
        })
      );
      await onClick();
    }
  };

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup || externalPopup.closed) {
        clearInterval(timer);
        setExternalPopup(null);
        return;
      }

      try {
        const currentUrl = externalPopup.location.href;
        if (!currentUrl) {
          return;
        }
        const searchParams = new URL(currentUrl).searchParams;
        const authCode = searchParams.get("auth_code");
        const state = searchParams.get("state");

        if (authCode && state) {
          externalPopup.close();
          setExternalPopup(null);
          clearInterval(timer);

          onGetTokenTiktokBusiness({
            authCode,
            state,
          });
        }
      } catch {
        if (externalPopup.closed) {
          clearInterval(timer);
          setExternalPopup(null);
        }
      }
    }, 500);

    return () => {
      timer && clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [externalPopup]);

  if (type === "icon") {
    return (
      <>
        {openBackDrop &&
          createPortal(
            <BackDropLoader
              open={openBackDrop}
              platform={TIKTOK_BUSINESS}
              from={modeBackDrop}
            />,
            document.body
          )}
        <IconButton
          id={id}
          disableRipple={disableRipple}
          sx={{ ...sx }}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (prevBlockClick) {
              onPrevBlockClick();
              return;
            }

            if (
              requirePlatformConnection &&
              ((!isTikTokBusinessConnected && mode === "verify") ||
                mode === "add")
            ) {
              await onConnectAccount();
              return;
            }

            setIsLoading(true);
            onClick();
            setIsLoading(false);
          }}
        >
          {isLoading ? <Loader hasMarginTop={false} size={20} /> : icon}
        </IconButton>
      </>
    );
  }

  return (
    <>
      {openBackDrop &&
        createPortal(
          <BackDropLoader
            open={openBackDrop}
            platform={TIKTOK_BUSINESS}
            from={modeBackDrop}
          />,
          document.body
        )}
      <Button
        id={id}
        disabled={disabled}
        loading={isLoading}
        color="secondary"
        sx={{
          ...sx,
        }}
        onClick={async (e) => {
          e.preventDefault();
          if (prevBlockClick) {
            onPrevBlockClick();
            return;
          }

          if (
            requirePlatformConnection &&
            ((!isTikTokBusinessConnected && mode === "verify") ||
              mode === "add")
          ) {
            await onConnectAccount();
            return;
          }

          setIsLoading(true);
          await onClick();
          setIsLoading(false);
        }}
      >
        {showButtonIcon && icon}
        {showLogo && (
          <Avatar
            src={logoPlatform(logo)}
            variant="square"
            sx={{
              width: "20px",
              height: "20px",
              mr: 1,
              ".MuiAvatar-img": {
                objectFit: "contain",
              },
            }}
          />
        )}
        {doTranslation ? t(text) : text}
      </Button>
    </>
  );
};

export default ButtonTikTokBusinessAction;
